import * as React from "react";
import classNames from "classnames";
import { DATATEST } from "constants/integration";
import { formatMessage } from "lib/globalize-util";
import { LeftArrow } from "components/icons/assortments/left-arrow";
import { TargetsContainer } from "./targets-container";
import { List, Map } from "immutable";
import { TargetWizardContextProvider } from "../context/target-wizard-context";
import { FinancialTargetForm } from "../financial-target-form/financial-target-form";
import { FinancialTargetGridControl } from "../financial-target-grid-control";
import { ManageDistributionModal } from "./manage-distribution-modal/manage-distribution-modal";

type LeftSideProps = {
    isTargetFeatureEnabled: boolean;
    isRollupMode: boolean;
    isSaving: boolean;
    canShopBrand: boolean;
    savingCount?: number;
    mutationLastSentAt?: number;
    collectionSyncErrorThrow: (error: Error) => void;
    schema: List<Map<string, any>>;
    merchantId: string;
    handleContinueShopping: (e: React.MouseEvent) => void;
    isDistributionModalOpen: boolean;
};

export const LeftSide = ({
    isTargetFeatureEnabled,
    isRollupMode,
    collectionSyncErrorThrow,
    isSaving,
    savingCount,
    mutationLastSentAt,
    schema,
    merchantId,
    canShopBrand,
    handleContinueShopping,
    isDistributionModalOpen,
}: LeftSideProps) => {
    const notifyUserAboutDataLoss = mutationLastSentAt && mutationLastSentAt < Date.now() - 120000;
    useCollectionSyncErrorThrowEffect(collectionSyncErrorThrow, notifyUserAboutDataLoss);

    useTrackPendingChangesEffect(notifyUserAboutDataLoss);

    return (
        <div>
            <div className="leftSide">
                {!isRollupMode &&
                    (isTargetFeatureEnabled ? (
                        // If Target is enabled
                        <>
                            <FinancialTargetGridControl />
                            <TargetWizardContextProvider>
                                {/* Manage Targets table */}
                                <TargetsContainer isRollupMode={isRollupMode} />
                                <FinancialTargetForm schema={schema} merchantId={merchantId} />
                                {isDistributionModalOpen && <ManageDistributionModal />}
                            </TargetWizardContextProvider>
                        </>
                    ) : (
                        // If Target is NOT enabled
                        <button
                            disabled={isRollupMode || !canShopBrand}
                            data-test={DATATEST.ASSORTMENTS.CONTINUE_SHOPPING_BTN}
                            className={classNames("continueShopping", { disabled: isRollupMode || !canShopBrand })}
                            onClick={handleContinueShopping}
                        >
                            <LeftArrow />
                            <span>{formatMessage("Continue Shopping")}</span>
                        </button>
                    ))}

                {!isSaving && !!savingCount && (
                    <div
                        data-test={DATATEST.ASSORTMENTS.FOOTER.PENDING_CHANGES}
                        className={classNames("saving-badge", { "saving-badge-error": notifyUserAboutDataLoss })}
                    >
                        {savingCount}{" "}
                        {savingCount === 1 ? formatMessage("pending change") : formatMessage("pending changes")}
                    </div>
                )}
                {isSaving && <span className="savingNotification">{formatMessage("Saving changes...")}</span>}
            </div>
        </div>
    );
};

const useCollectionSyncErrorThrowEffect = (
    collectionSyncErrorThrow: (error: Error) => void,
    notifyUserAboutDataLoss: boolean
) => {
    React.useEffect(() => {
        if (notifyUserAboutDataLoss) {
            collectionSyncErrorThrow(
                new Error("There is a problem saving your data. Please contact support at heroes@nuorder.com for help.")
            );
        }
    }, [notifyUserAboutDataLoss, collectionSyncErrorThrow]);
};

const useTrackPendingChangesEffect = (notifyUserAboutDataLoss: boolean) => {
    const analytics = (global as any).analytics as any;

    const init = React.useRef(false);
    React.useEffect(() => {
        if (init.current && analytics && notifyUserAboutDataLoss) {
            analytics.track("ASSORTMENT_PENDING_CHANGES_RED");
            return () => {
                if (!notifyUserAboutDataLoss) {
                    analytics.track("ASSORTMENT_PENDING_CHANGES_GREEN");
                }
            };
        }
        init.current = true;
    }, [notifyUserAboutDataLoss, analytics]);
};
